import type { SubmitEventHandler } from 'react';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { getTestProps } from '../../lib/helpers';
import { passwordSchema } from '../../lib/zodHelpers';

import Button from '../../components/Button/Button';
import Loader from '../../components/Loader/Loader';

import { WarningIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const initialValues = { plainPassword: '' };

interface ChangePasswordFormProps {
  /**
   * ChangePasswordRequestForm on submit handler
   */
  onSubmit: (
    password: string,
  ) => Promise<undefined | Record<string, string | string[]>>;
  /**
   * Form test id
   */
  testId?: string;
}

const ChangePasswordForm = ({
  onSubmit,
  testId = '',
}: ChangePasswordFormProps) => {
  const { t } = useTranslation();
  const [generalError, setGeneralError] = useState<string | string[]>();
  const [isLoading, setIsLoading] = useState(false);

  const validationSchema = useMemo(
    () =>
      z.object({
        plainPassword: passwordSchema(t),
      }),
    [t],
  );

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      if (!onSubmit) return null;
      setIsLoading(true);
      const errors = await onSubmit(value.plainPassword);

      if (errors && Object.keys(errors).length > 0) {
        if (errors.global || errors.token) {
          setGeneralError(errors.global || errors.token);
        }
        setFormSubmitErrors(formApi, errors);
      }
      setIsLoading(false);
    },
    validators: {
      onSubmit: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id={'change-password-from'}
        className="w-full max-w-md m-auto"
        onSubmit={handleSubmit}
        noValidate={true}
        {...getTestProps(testId)}
      >
        <div
          className="flex flex-wrap mb-8 w-full m-auto max-w-[300px]"
          {...getTestProps(testId, 'password-container')}
        >
          <form.AppField name="plainPassword">
            {(field) => (
              <field.Input
                type="password"
                placeholder={t('Global.Password')}
                showPasswordInfo
              />
            )}
          </form.AppField>
        </div>

        <div className="w-full text-center">
          <Button
            type="submit"
            buttonSize="lg"
            additionalClasses="m-auto min-w-[300px] justify-center"
            additionalChildrenClasses="flex flex-row items-center"
          >
            {t('Global.Save')}
            {isLoading && (
              <Loader
                size="tiny"
                type="spinner-white"
                additionalClasses="h-5 ml-2"
              />
            )}
          </Button>
          {generalError && (
            <div className="text-red py-2 inline-flex">
              <WarningIcon className="h-5 w-5 mr-2" />
              <div className="block text-left">{generalError}</div>
            </div>
          )}
        </div>
      </form>
    </form.AppForm>
  );
};
export default ChangePasswordForm;
